import { Link } from "@mui/material";
import { cn } from "@utils";
import { ScrollText } from "lucide-react";
import { ReactNode, useId } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Pill } from "src/components/ui/Pill";
import {
  ChangeManagementImpactLevel,
  ChangeManagementPlan,
} from "src/types/change-management";
import { getChangeTypeDisplay, getSourceIcon } from "./change-management-plan";
import { EvidenceLink } from "./EvidenceLink";
import { ImpactPill } from "./ImpactPill";
import { PlanSection } from "./PlanSection";
import { ReviewActionPlaceholders } from "./ReviewActionPlaceholders";
import { PlanDocumentResolver } from "./useResolvedPlanDocuments";

const impactBoxClassName: Record<
  NonNullable<ChangeManagementImpactLevel> | "unknown",
  string
> = {
  low: "border-green-200 bg-green-50",
  medium: "border-yellow-300 bg-yellow-50",
  high: "border-red-200 bg-red-50",
  unknown: "border-black/[.12] bg-color-bg",
};

const planReadiness: Record<
  ChangeManagementPlan["status"],
  { label: string; className: string; description: string }
> = {
  ok: {
    label: "Ready",
    className: "border-green-500 bg-green-100 text-green-700",
    description: "The agent found the evidence it needed to plan this change.",
  },
  partial: {
    label: "Could not complete",
    className: "border-orange-300 bg-orange-100 text-orange-800",
    description:
      "Missing evidence could change the procedure route, the impact or the suggested changes. See Open issues.",
  },
};

const SummaryBlock = ({
  title,
  badge,
  className,
  children,
}: {
  title: string;
  badge?: ReactNode;
  className?: string;
  children: ReactNode;
}) => {
  const headingId = useId();
  return (
    <section
      aria-labelledby={headingId}
      className={cn("flex min-w-0 flex-col gap-1.5", className)}
    >
      <div className="flex flex-wrap items-center gap-2.5">
        <h3
          id={headingId}
          className="m-0 text-base font-semibold leading-[1.5] text-black/[.87]"
        >
          {title}
        </h3>
        {badge}
      </div>
      {children}
    </section>
  );
};

const SummaryText = ({ children }: { children: ReactNode }) => (
  <p className="m-0 max-w-[78ch] leading-[1.6]">{children}</p>
);

const SummarySubsection = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => {
  const headingId = useId();
  return (
    <section aria-labelledby={headingId} className="mt-1.5 flex flex-col gap-1">
      <h4
        id={headingId}
        className="m-0 text-sm font-semibold leading-[1.5] text-black/60"
      >
        {title}
      </h4>
      {children}
    </section>
  );
};

const SourcesOfChange = ({
  sources,
}: {
  sources: ChangeManagementPlan["change_documentation_sources"];
}) => (
  <SummarySubsection title="Sources of change">
    {sources.length ? (
      <ul className="m-0 flex list-none flex-col gap-1 p-0">
        {sources.map((source, index) => {
          const SourceIcon = getSourceIcon(source.system);
          return (
            <li key={index} title={source.system} className="flex">
              <EvidenceLink
                href={source.url}
                label={source.refTitle}
                icon={SourceIcon}
              />
            </li>
          );
        })}
      </ul>
    ) : (
      <span className="text-sm text-black/60">
        No change sources were recorded.
      </span>
    )}
  </SummarySubsection>
);

const ProceduresApplied = ({
  processSources,
  resolveDocument,
}: {
  processSources: ChangeManagementPlan["process_sources"];
  resolveDocument: PlanDocumentResolver;
}) => {
  const procedures = [...processSources].sort(
    (a, b) => Number(b.role === "governing") - Number(a.role === "governing")
  );
  return (
    <SummarySubsection title="Procedures applied">
      <ul className="m-0 flex list-none flex-col gap-1 p-0">
        {procedures.map((source, index) => {
          const resolved =
            source.document_id && source.version_id
              ? resolveDocument(source.document_id, source.version_id)
              : null;
          return (
            <li key={index} className="flex items-center gap-2">
              {resolved ? (
                <Link
                  component={RouterLink}
                  to={resolved.href}
                  underline="hover"
                  className="inline-flex items-center gap-[5px] text-sm"
                >
                  <ScrollText size={12} className="text-gray-500" />
                  {source.title}
                </Link>
              ) : (
                <EvidenceLink
                  href={source.url}
                  label={source.title}
                  icon={ScrollText}
                />
              )}
              {source.role === "governing" && (
                <span className="rounded bg-gray-100 px-1.5 text-xs font-semibold leading-[1.66] text-black/60">
                  Governing
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </SummarySubsection>
  );
};

export const PlanOverviewCard = ({
  plan,
  resolveDocument,
}: {
  plan: ChangeManagementPlan;
  resolveDocument: PlanDocumentResolver;
}) => {
  const changeType = getChangeTypeDisplay(plan.change_type);
  const readiness = planReadiness[plan.status];
  return (
    <PlanSection
      title="Summary"
      actions={<ReviewActionPlaceholders showReject={false} />}
    >
      <div className="flex flex-col gap-4 rounded border border-solid border-black/[.12] bg-white p-5">
        <SummaryBlock
          title="Product changes"
          badge={
            <Pill
              className="border-gray-300 bg-gray-100 text-black/60"
              description={changeType.description}
            >
              {changeType.label}
            </Pill>
          }
        >
          <SummaryText>{plan.summary.product}</SummaryText>
          <SourcesOfChange sources={plan.change_documentation_sources} />
        </SummaryBlock>
        <SummaryBlock
          title="Implementation plan"
          badge={
            <Pill
              className={readiness.className}
              description={readiness.description}
            >
              {readiness.label}
            </Pill>
          }
          className="border-0 border-t border-solid border-black/[.12] pt-4"
        >
          <SummaryText>{plan.summary.plan}</SummaryText>
          <ProceduresApplied
            processSources={plan.process_sources}
            resolveDocument={resolveDocument}
          />
        </SummaryBlock>
        <SummaryBlock
          title="Impact assessment"
          badge={<ImpactPill level={plan.summary.impact.level} />}
          className={cn(
            "rounded border border-solid p-4",
            impactBoxClassName[plan.summary.impact.level ?? "unknown"]
          )}
        >
          <SummaryText>{plan.summary.impact.why}</SummaryText>
        </SummaryBlock>
      </div>
    </PlanSection>
  );
};
